// =============================================================================
// app/src/lib/marcasDasCalotas.ts — a calota mora na ÁREA DA MARCA, não num gancho.
//
// Pedido do dono (15/10/2026): calota gira muito — vendeu um jogo, outro modelo
// entra no gancho — e o endereço gancho a gancho fica velho no dia seguinte
// (em 15/10, 9 dos 67 ganchos já não batiam com o que ele pintou). A loja já
// se organiza por MARCA, parede a parede: Ford e Fiat na parede da entrada,
// Volkswagen e GM na da direita, os importados em cima da porta e na parede
// vermelha. Então o lugar da calota passa a ser a área da marca dela:
//
//   - a MARCA de cada gancho mora na planta (`marca` no objeto 'calotas'),
//     editável no mapa por dono e gerente, com versão e "voltar" de graça;
//   - a marca de cada CALOTA sai do carro em que ela serve (product_
//     applications.montadora) — ninguém cadastra lugar ao trocar o gancho.
//
// Calota sem carro (universal, como a Orbital) e marca sem gancho marcado no
// mapa continuam com o lugar de sempre (stock.local).
// =============================================================================
import type { PlantaDaLoja } from './loja3d';

/** Os cinco grupos com que o dono organiza a loja, na ordem dele. */
export type MarcaDaCalota = 'volkswagen' | 'gm' | 'fiat' | 'ford' | 'importados';

export const MARCAS_DAS_CALOTAS: ReadonlyArray<{ id: MarcaDaCalota; nome: string }> = [
  { id: 'volkswagen', nome: 'Volkswagen' },
  { id: 'gm', nome: 'GM' },
  { id: 'fiat', nome: 'Fiat' },
  { id: 'ford', nome: 'Ford' },
  { id: 'importados', nome: 'Importados' },
];

/**
 * A cor de cada marca (16/10/2026): a mesma da página em que o dono pintou os
 * ganchos. É com ela que o mapa pinta cada calota — no 3D, na parede de frente
 * e na legenda —, no lugar da foto: a foto do gancho mentia a cada jogo
 * vendido, a cor da marca não. `nome` vai na frase ("os ganchos da Ford são os
 * verdes"); `texto` é a cor da letra por cima.
 */
export const COR_DA_MARCA: Record<MarcaDaCalota, { cor: string; texto: string; nome: string }> = {
  volkswagen: { cor: '#1E4FA3', texto: '#FFFFFF', nome: 'azuis' },
  gm: { cor: '#E3A21A', texto: '#141414', nome: 'amarelos' },
  fiat: { cor: '#B3122E', texto: '#FFFFFF', nome: 'vermelhos' },
  ford: { cor: '#0F7B6C', texto: '#FFFFFF', nome: 'verdes' },
  importados: { cor: '#6B4FA0', texto: '#FFFFFF', nome: 'roxos' },
};

export function ehMarcaDaCalota(v: unknown): v is MarcaDaCalota {
  return MARCAS_DAS_CALOTAS.some((m) => m.id === v);
}

export function nomeDaMarca(m: MarcaDaCalota): string {
  return MARCAS_DAS_CALOTAS.find((x) => x.id === m)!.nome;
}

const semAcento = (t: string) => t.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

/**
 * A montadora como o catálogo grava ("VW - VolksWagen", "GM - Chevrolet",
 * "Fiat", "Ford", "Renault", "Hyundai") no grupo da loja. Toda montadora que
 * não é uma das quatro é IMPORTADOS — é assim que o balcão chama Renault,
 * Hyundai, Peugeot, Toyota e as demais.
 */
export function marcaDaMontadora(montadora: string | null | undefined): MarcaDaCalota | null {
  const t = semAcento(montadora ?? '');
  if (!t) return null;
  if (t.includes('volks') || /^vw\b/.test(t)) return 'volkswagen';
  if (t.includes('chevrolet') || /^gm\b/.test(t)) return 'gm';
  if (t.includes('fiat')) return 'fiat';
  if (t.includes('ford')) return 'ford';
  return 'importados';
}

/**
 * A marca de UMA calota pelas montadoras em que ela serve: a que aparece mais
 * vezes; empate, a primeira na ordem do dono. Sem montadora (universal): null.
 */
export function marcaDaCalota(montadoras: readonly (string | null | undefined)[]): MarcaDaCalota | null {
  const conta = new Map<MarcaDaCalota, number>();
  for (const m of montadoras) {
    const g = marcaDaMontadora(m);
    if (g) conta.set(g, (conta.get(g) ?? 0) + 1);
  }
  let melhor: MarcaDaCalota | null = null;
  for (const { id } of MARCAS_DAS_CALOTAS) {
    const n = conta.get(id) ?? 0;
    if (n > 0 && (melhor === null || n > conta.get(melhor)!)) melhor = id;
  }
  return melhor;
}

/** Uma parede da área: o nome como a loja fala e os ganchos dela, em ordem de código. */
export interface ParedeDaArea {
  nome: string;
  ganchos: string[];
}

/** A área de uma marca: onde ficam as calotas dela. */
export interface AreaDaMarca {
  marca: MarcaDaCalota;
  nome: string;
  paredes: ParedeDaArea[];
}

/**
 * O nome do gancho na planta é "Calotas E · parede da entrada": a parede é o
 * que vem depois do ponto. Sem ponto, o nome inteiro; sem nome, "ganchos".
 */
export function nomeDaParede(nome: string | null | undefined): string {
  const t = (nome ?? '').trim();
  if (!t) return 'ganchos';
  const i = t.indexOf('·');
  return (i >= 0 ? t.slice(i + 1) : t).trim() || 'ganchos';
}

/** As áreas que a planta marca: ganchos de calota com `marca` e código, agrupados por parede. */
export function areasDaPlanta(planta: PlantaDaLoja): Map<MarcaDaCalota, AreaDaMarca> {
  const porMarca = new Map<MarcaDaCalota, Map<string, string[]>>();
  for (const o of planta.objetos) {
    if (o.tipo !== 'calotas' || !o.marca || !o.codigo) continue;
    const paredes = porMarca.get(o.marca) ?? new Map<string, string[]>();
    const parede = nomeDaParede(o.nome);
    paredes.set(parede, [...(paredes.get(parede) ?? []), o.codigo.trim().toUpperCase()]);
    porMarca.set(o.marca, paredes);
  }
  const areas = new Map<MarcaDaCalota, AreaDaMarca>();
  for (const { id, nome } of MARCAS_DAS_CALOTAS) {
    const paredes = porMarca.get(id);
    if (!paredes) continue;
    const comparar = (a: string, b: string) => a.localeCompare(b, 'pt-BR', { numeric: true });
    areas.set(id, {
      marca: id,
      nome,
      paredes: [...paredes.entries()]
        .map(([p, g]) => ({ nome: p, ganchos: [...new Set(g)].sort(comparar) }))
        .sort((a, b) => comparar(a.ganchos[0]!, b.ganchos[0]!)),
    });
  }
  return areas;
}

/** "parede da entrada" ou "parede da direita e em cima da porta". */
export function ondeFicaAArea(area: AreaDaMarca): string {
  const nomes = area.paredes.map((p) => p.nome);
  return nomes.length <= 1 ? (nomes[0] ?? '') : `${nomes.slice(0, -1).join(', ')} e ${nomes[nomes.length - 1]}`;
}

/** "da Ford", "da GM", "dos importados" — para a frase da folha. */
export function daMarca(area: Pick<AreaDaMarca, 'marca' | 'nome'>): string {
  return area.marca === 'importados' ? 'dos importados' : `da ${area.nome}`;
}

/** Todos os ganchos da área, parede a parede. */
export function ganchosDaArea(area: AreaDaMarca): string[] {
  return area.paredes.flatMap((p) => p.ganchos);
}
